type TaskCallback<T> = () => Promise<T>;
type TaskRequest<T> = {
    callback: TaskCallback<T>;
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    retries: number;
};

/**
 * The TaskQueue is a utility that helps manage a set of tasks using a limited resource. It can be helpful
 * for limiting network requests (where browsers cap the number of simultaneous requests) or act as a task queue
 * to ensure no simultaneous operations on a Luminary instance.
 */
export class TaskQueue {
    classType = 'TaskQueue';

    MAX_SIMULTANEOUS_OPERATIONS: number;
    MAX_RETRIES: number;

    /**
     * A list of delays to use for each retry. If not provided, the delays will be 0 for each retry.
     */
    RETRY_SCHEDULE: number[];

    /**
     * A list of task callbacks that have been received and not yet triggered
     */
    #tasks: TaskRequest<unknown>[] = [];
    /**
     * A list of requests which are currently in progress
     */
    #inProgressTasks: TaskRequest<unknown>[] = [];

    #interval: number | null = null;
    #scheduledRetries: number[] = [];

    constructor(
        max_simultaneous_operations: number,
        max_retries: number,
        retry_schedule?: number[],
    ) {
        this.MAX_SIMULTANEOUS_OPERATIONS = max_simultaneous_operations;
        this.MAX_RETRIES = max_retries;
        if (retry_schedule) {
            if (retry_schedule.length !== max_retries) {
                throw new Error('retry_schedule must be the same length as max_retries');
            }
            this.RETRY_SCHEDULE = retry_schedule;
        } else {
            this.RETRY_SCHEDULE = Array(max_retries).fill(0);
        }
    }

    _boundCheckInProgressTasks = () => {
        this.#checkInProgressTasks();
    };

    destroy() {
        if (this.#interval) {
            window.clearInterval(this.#interval);
            this.#interval = null;
        }
        this.#scheduledRetries.forEach((timeoutId) => {
            window.clearTimeout(timeoutId);
        });
        this.#scheduledRetries = [];
    }

    /**
     * clears all tasks that are queued but haven't started processing
     * NOTE: make sure this is safe to do!
     */
    clearQueuedTasks() {
        this.#tasks = [];
    }

    /**
     * @param callback - the method to call when a queue slot is available. It should be called with a cleanup method that will free the slot
     */
    queueTask<T>(callback: TaskCallback<T>) {
        return new Promise<T>((resolve, reject) => {
            this.#tasks.push({
                callback,
                resolve: resolve as (value: unknown) => void,
                reject,
                retries: 0,
            });
            this.#checkInProgressTasks();
        });
    }

    #freeQueueSlot<T>(request: TaskRequest<T>) {
        const index = this.#inProgressTasks.indexOf(request);
        if (index !== -1) {
            this.#inProgressTasks.splice(index, 1);
            this.#checkInProgressTasks();
        }
    }

    #checkInProgressTasks() {
        // add new requests for any open slots
        while (
            this.#inProgressTasks.length < this.MAX_SIMULTANEOUS_OPERATIONS &&
            this.#tasks.length > 0
        ) {
            const request = this.#tasks.shift(); // grab first request
            if (!request) {
                // request removed, continue!
                continue;
            }
            this.#inProgressTasks.push(request);
            // call the callback, passing cleanup method as argument
            request
                .callback()
                .then((resolveVal) => {
                    request.resolve(resolveVal);
                    this.#freeQueueSlot(request);
                })
                .catch((error) => {
                    if (request.retries < this.MAX_RETRIES) {
                        const delay = this.RETRY_SCHEDULE[request.retries] || 0;
                        const timeoutId = window.setTimeout(() => {
                            this.#scheduledRetries.splice(
                                this.#scheduledRetries.indexOf(timeoutId),
                                1,
                            );
                            const repeatRequest = {
                                ...request,
                                retries: request.retries + 1,
                            };
                            this.#tasks.push(repeatRequest);
                            this.#checkInProgressTasks();
                        }, delay);
                        this.#scheduledRetries.push(timeoutId);
                    } else {
                        request.reject(error);
                    }
                    this.#freeQueueSlot(request);
                });
        }

        // register periodic checks for any in-progress tasks
        if (this.#inProgressTasks.length > 0 && this.#interval === null) {
            this.#interval = window.setInterval(this._boundCheckInProgressTasks, 100);
        }
        // conversely, unregister checks if nothing to check!
        else if (this.#inProgressTasks.length === 0 && this.#interval !== null) {
            window.clearInterval(this.#interval);
            this.#interval = null;
        }
    }
}
